import { API_HOST } from "@/config";

const ACCEPT_JSON_HEADERS = {
  Accept: "application/json",
};

const JSON_HEADERS = {
  ...ACCEPT_JSON_HEADERS,
  "Content-Type": "application/json",
};

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, body: unknown, message?: string) {
    super(message ?? `HTTP error ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

function formatUrl(baseUrl: string, path: string): string {
  const url = new URL(path, baseUrl);

  return url.toString();
}

function resolveErrorMessage(status: number, body: unknown): string {
  if (typeof body === "string") {
    return body;
  }

  if (body && typeof body === "object" && "message" in body) {
    return String((body as { message?: unknown }).message);
  }

  return `HTTP error ${status}`;
}

export async function get<T = unknown>(
  url: string,
  signal?: AbortSignal,
): Promise<T> {
  const response = await fetch(formatUrl(API_HOST, url), {
    headers: ACCEPT_JSON_HEADERS,
    signal,
  });

  if (!response.ok) {
    throw new Error("An error occurred while fetching the data.");
  }

  return response.json() as Promise<T>;
}

type Params = Record<string, unknown>;

export async function post<T = unknown>(
  url: string,
  data?: Params,
  signal?: AbortSignal,
): Promise<T> {
  const response = await fetch(formatUrl(API_HOST, url), {
    method: "POST",
    headers: JSON_HEADERS,
    ...(data && {
      body: JSON.stringify(data),
    }),
    signal,
  });

  if (!response.ok) {
    let body: unknown;
    try {
      body = await response.json();
    } catch {
      throw new ApiError(response.status, undefined);
    }

    throw new ApiError(
      response.status,
      body,
      resolveErrorMessage(response.status, body),
    );
  }

  return response.json() as Promise<T>;
}

export async function put<T = unknown>(url: string, data: T): Promise<T> {
  const response = await fetch(formatUrl(API_HOST, url), {
    method: "PUT",
    headers: JSON_HEADERS,
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error("An error occurred while putting the data.");
  }

  return response.json() as Promise<T>;
}

export async function remove(url: string): Promise<void> {
  const response = await fetch(formatUrl(API_HOST, url), {
    method: "DELETE",
    headers: ACCEPT_JSON_HEADERS,
  });

  if (!response.ok) {
    throw new Error("An error occurred while deleting the data.");
  }

  return;
}
